/**
 * @file reportCurrency.ts
 * @brief Reading per-currency report rows without folding them into one number.
 *
 * @details
 * - What the file does: Groups report rows by their own `currency` and picks a
 *   primary, so a KPI card can show a real total and disclose the rest.
 * - Where it fits: Company and Admin report components. Backend report
 *   aggregators now group by currency (24-08-2026) instead of returning a
 *   single currency-blind sum.
 * - Important technical decisions: Before this, every report component summed
 *   minor units across all rows and rendered them with a hard-coded `"GBP"` —
 *   25 call sites. On the SUPER ADMIN reports that total spans every tenant on
 *   the platform, so one non-GBP company made revenue and margin wrong, not
 *   merely mislabelled. Rows without a currency fall into the platform default
 *   rather than being dropped.
 *
 * @references
 * - BACKEND/src/features/company/reports.service.js
 * - BACKEND/src/features/admin/service.js getRevenueByProductReport
 * - shared/utils/invoiceTotals.ts (the same idea for the invoice feed)
 *
 * @author DES
 * @date 24-08-2026
 */

const FALLBACK_CURRENCY = "GBP";

export type CurrencyGroup<T> = {
  currency: string;
  rows: T[];
};

export type GroupedByCurrency<T> = {
  /** Currency of the largest group by `weigh`. */
  primary: string;
  /** Rows in the primary currency — what a single-currency UI should render. */
  primaryRows: T[];
  /** Every group, largest first. */
  groups: CurrencyGroup<T>[];
  /** True when more than one currency is present, so the UI must say so. */
  mixed: boolean;
};

function currencyOf(row: unknown): string {
  const value = (row as { currency?: unknown })?.currency;
  return typeof value === "string" && value.trim()
    ? value.trim()
    : FALLBACK_CURRENCY;
}

/**
 * Groups `rows` by their `currency`, ordering groups by total `weigh` desc.
 *
 * `weigh` should return the row's money value so the biggest currency leads;
 * pass a constant to order by row count instead.
 */
export function groupRowsByCurrency<T>(
  rows: readonly T[],
  weigh: (row: T) => number,
): GroupedByCurrency<T> {
  const byCurrency = new Map<string, T[]>();
  for (const row of rows) {
    const key = currencyOf(row);
    const bucket = byCurrency.get(key);
    if (bucket) bucket.push(row);
    else byCurrency.set(key, [row]);
  }

  const groups = Array.from(byCurrency.entries())
    .map(([currency, groupRows]) => ({
      currency,
      rows: groupRows,
      weight: groupRows.reduce((sum, r) => sum + (weigh(r) || 0), 0),
    }))
    .sort((a, b) => b.weight - a.weight)
    .map(({ currency, rows: groupRows }) => ({ currency, rows: groupRows }));

  return {
    primary: groups[0]?.currency ?? FALLBACK_CURRENCY,
    primaryRows: groups[0]?.rows ?? [],
    groups,
    mixed: groups.length > 1,
  };
}

/** A row's own currency, for rendering that single row's amounts. */
export function rowCurrency(row: unknown): string {
  return currencyOf(row);
}
